import type {
  IContractChangeHistoryApi,
  IContractDetailsApi,
  IPaymentScheduleApi,
  IPaymentScheduleStatusApi
} from '@/pages/ContractInfo/types/contract.types';
import type { ITerminateCalculationApi } from '@/pages/ContractInfo/types/terminate.types';
import type { IContractListApi } from '@/pages/Contracts/types/contracts-list.types';
import type {
  EPaymentStatus,
  ETerminationReason
} from '@/shared/constants/contracts';

import { ApiConnector } from './api.connector';

class ContractsApi extends ApiConnector {
  getContracts(params?: IGetContractsParams): Promise<IContractListApi> {
    return this.call({ path: 'contracts/find-all', params });
  }

  getContractInfo(id: string): Promise<IContractDetailsApi> {
    return this.call({ path: `contracts/${id}` });
  }

  getContractHistory(contractId: string): Promise<IContractChangeHistoryApi> {
    return this.call({ path: `contracts/${contractId}/history` });
  }

  getPaymentSchedule(
    params: IGetPaymentScheduleParams
  ): Promise<IPaymentScheduleApi> {
    return this.call({
      path: `payment-schedule/find-by-contract`,
      params
    });
  }

  updatePaymentScheduleStatus(
    paymentUuid: string,
    body: IUpdatePaymentScheduleStatusBody
  ): Promise<IPaymentScheduleStatusApi> {
    return this.call({
      path: `payment-schedule/${paymentUuid}/status`,
      method: 'patch',
      body
    });
  }

  getTerminateCalculation(
    params: ITerminateCalculationParams
  ): Promise<ITerminateCalculationApi> {
    const { contractId, terminationDate } = params;
    return this.call({
      path: `contracts/${contractId}/terminate-calculate`,
      params: { terminationDate }
    });
  }

  createTerminationRequest(
    contractId: string,
    body: ICreateTerminationRequestBody
  ): Promise<unknown> {
    return this.call({
      path: `contracts/${contractId}/termination`,
      method: 'post',
      body
    });
  }

  cancelTerminationRequest(
    contractId: string,
    body: { description: string }
  ): Promise<unknown> {
    return this.call({
      path: `contracts/${contractId}/termination/cancel`,
      method: 'post',
      body
    });
  }

  finalizeTerminationRequest(
    contractId: string,
    data: { image: File }
  ): Promise<unknown> {
    const formData = new FormData();
    formData.append('image', data.image);
    return this.call({
      path: `contracts/${contractId}/termination/finalize`,
      method: 'post',
      body: formData
    });
  }
}

export const contractsApi = new ContractsApi();

interface IGetContractsParams {
  offset: number;
  limit: number;
  searchTerm?: string;
  order?: string;
  statusId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface IGetPaymentScheduleParams {
  contract_uuid: string;
  offset: number;
  limit: number;
}

export interface IUpdatePaymentScheduleStatusBody {
  status: EPaymentStatus;
}

interface ITerminateCalculationParams {
  contractId: string;
  terminationDate: string;
}

export interface ICreateTerminationRequestBody {
  terminationDate: string;
  reason: ETerminationReason;
  proportionalPayment: string;
  proportionalInterest: string;
  daysUsed: number;
  periodNumber: number;
  unpaidPeriodsTotal: string;
  penaltyAmount: string;
  totalAmount: string;
}
